import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useStickyScroll } from '@aimsos/hooks';
import { useTranslation } from '@aimsos/i18n';
import { BreadcrumbNav, CartContainer, Skeleton, Tab, TabNav } from '@aimsos/ui';

import { AccessDeniedState, ErrorState, NotFoundState } from '../components/states/index.js';
import EntityHeader from '../components/EntityHeader.jsx';
import ProfileAskButton from '../components/ProfileAskButton.jsx';
import ProfileItemSlideOut from '../components/item-slideout/ProfileItemSlideOut.jsx';
import NextBestActionSlideOut from '../components/next-best-action/NextBestActionSlideOut.jsx';
import ProfileSummaryCards from '../components/summary/ProfileSummaryCards.jsx';
import SummaryCollapseToggle from '../components/summary/SummaryCollapseToggle.jsx';
import { useEntityHeader } from '../hooks/useEntityHeader.js';
import { useProfileHowToApproach } from '../hooks/useProfileHowToApproach.js';
import { useProfileNextBestActions } from '../hooks/useProfileNextBestActions.js';
import { useProfileSummary } from '../hooks/useProfileSummary.js';
import { useProfileTabs } from '../hooks/useProfileTabs.js';
import { OVERVIEW_TAB, TAB_KINDS } from '../constants/profileTabs.js';
import { useProfileTypes } from '../hooks/useProfileTypes.js';
import { workflowChipLabel } from '../utils/workflowChipLabel.js';

import { Link2 } from 'lucide-react';

/**
 * Screen 3 of 3 — the shell around one entity's profile.
 *
 * The tabs are CHILD ROUTES, not local state. That is what makes a profile
 * linkable, Back and Forward behave, and a pasted URL land on the tab it names.
 *
 * The entity header is fetched HERE, once, and handed down through outlet
 * context. A tab fetching its own would re-request on every tab switch —
 * wasteful, and visible as a flicker. There is a test pinning the call count.
 *
 * THE PAGE OWNS THE ONLY SCROLL CONTAINER (CT-5526). The identity card is
 * sticky inside it and compresses once the profile has moved — the same
 * behaviour, from the same `useStickyScroll`, as the list pages
 * (`.cursor/rules/list-page.mdc`). The tab bar sits below the card and scrolls
 * away; the active tab flows inside the one scroller and must NOT bring its own,
 * or the card would pin against a container that never scrolls.
 */
function UnifiedProfilePage() {
  const { t } = useTranslation('unified-profiles');
  const { entityType, entityId, tabId } = useParams();
  const navigate = useNavigate();

  const { types } = useProfileTypes();
  const { tabs, loading: tabsLoading } = useProfileTabs({ entityType });
  const { entity, loading, error, notFound, accessDenied, refresh, reload } = useEntityHeader({
    entityType,
    entityId,
  });

  const scrollRef = useRef(null);
  const { isScrolled } = useStickyScroll(scrollRef);

  // A tab's own toolbar (currently just Overview's dashboard picker + Edit)
  // portals in here, so it sits on the SAME row as the tabs, space-between,
  // instead of stacking a second row underneath them. `null` until the ref
  // callback fires; a tab with nothing to show there (Activity) never renders
  // into it.
  const [tabActionsSlot, setTabActionsSlot] = useState(null);

  const typeLabel = useMemo(
    () => types.find((type) => type.id === entityType)?.label ?? entityType,
    [types, entityType],
  );

  // The tab is the `:tabId` PARAM, never the last path segment: a tab with child
  // routes (Intelligence → `intelligence/traits`) would otherwise select a tab
  // called "traits" and reset the scroll on every subtab switch (CT-5715).
  const activeTabId = tabId;

  // Every tab shares the one scroll container, which stays mounted across tab
  // changes, so its `scrollTop` would carry over: a tab opened after scrolling
  // down would land mid-content, its filter bar and first rows already under
  // the pinned header. Returning to the top on each tab change covers the
  // click AND the arrow-key path, since both move `activeTabId`.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (scroller) scroller.scrollTop = 0;
  }, [activeTabId]);

  // A tab id this entity type does not have is not an error — a link can
  // outlive a tenant's configuration — so it falls back to the invariant.
  const unknownTab =
    !tabsLoading && Boolean(tabId) && !tabs.some((tab) => tab.id === tabId);

  // Relative from the layout route — never rebuilt from `/unified-profiles/...`.
  const goToTab = useCallback((tab) => navigate(tab), [navigate]);
  // For a tab that sends the user to ANOTHER tab with a request in the query
  // (Traits → `activity?new=note`, CT-5715). Same relative base as `goToTab`.
  const openTab = useCallback(
    (tab, search = '') => navigate({ pathname: tab, search }),
    [navigate],
  );
  // `..` from here climbs the whole layout route — whose path is TWO segments
  // (`:entityType/:entityId`) — and lands on the Records list, not on the list of the type the user
  // came from. Naming the type segment explicitly keeps it relative while
  // going back exactly one screen.
  const handleBackToList = useCallback(
    () => navigate(`../${entityType}`),
    [navigate, entityType],
  );
  const onBreadcrumbNavigate = useCallback((path) => navigate(path), [navigate]);

  // The record's name wherever the page shows it: breadcrumb, header and Ask.
  const displayTitle = entity?.title ?? t('profile.untitled');

  const breadcrumbItems = useMemo(() => {
    const root = { label: t('list.title'), path: '..' };
    const type = { label: typeLabel, path: `../${entityType}` };

    if (notFound || accessDenied || error) {
      return [root, type, { label: t('profile.breadcrumbUnavailable') }];
    }

    if (loading || !entity) {
      return [
        root,
        type,
        {
          label: (
            <span
              data-testid="profile-breadcrumb-skeleton"
              className="inline-flex min-w-[140px] align-middle"
            >
              <Skeleton height="16px" width="160px" borderRadius="4px" />
            </span>
          ),
        },
      ];
    }

    return [root, type, { label: displayTitle }];
  }, [t, typeLabel, entityType, notFound, accessDenied, error, loading, entity, displayTitle]);

  // Where an item's "Open in History" leads (CT-5716): the designed feed tab,
  // whatever the tenant calls it.
  const historyTabId = useMemo(() => tabs.find((tab) => tab.kind === TAB_KINDS.ACTIVITY)?.id ?? null, [tabs]);

  // Ask lives in its own component so the shell chat it follows never
  // re-renders this page (CT-5721).
  const hasEntity = Boolean(entity);
  const askAction = useMemo(
    () =>
      hasEntity ? <ProfileAskButton entityType={entityType} entityId={entityId} title={displayTitle} /> : null,
    [hasEntity, entityType, entityId, displayTitle],
  );

  // The Concierge cards under the header, their slide-out and Start workflow
  // (CT-5713): one hook, fetched once per profile and scoped to the record. A
  // started workflow re-reads the header quietly for its chip.
  const nba = useProfileNextBestActions({ entityType, entityId, entityName: displayTitle, onStarted: reload });
  // How to approach beside it (CT-5714), also fetched once per profile; a row
  // opens its trait or insight in the profile slide-out.
  const approach = useProfileHowToApproach({ entityType, entityId });
  // The two cards as one row: one state, one Collapse, one Retry.
  const summary = useProfileSummary({ nba, approach });
  // Tabs open the same slide-out for a suggestion they list (Tasks' Review workflow,
  // CT-5722) and hand the Concierge the same record, under the same name.
  // A started workflow drops its suggestion, so those tabs re-read (`workflowsStarted`).
  const { openPanel: openNbaPanel, conciergeEntity, workflowsStarted: nbaWorkflowsStarted } = nba;
  // An item changed from the profile slide-out (a report, a resolved conflict):
  // How to approach re-reads, and `itemsVersion` tells the open tab (CT-5718).
  const [itemsVersion, setItemsVersion] = useState(0);
  const { refresh: refreshApproach } = approach;
  const handleItemChanged = useCallback(() => {
    setItemsVersion((value) => value + 1);
    refreshApproach();
  }, [refreshApproach]);

  const workflowLabel = workflowChipLabel(entity?.workflow ?? null, t);
  const workflowChip = useMemo(() => (workflowLabel ? { label: workflowLabel } : undefined), [workflowLabel]);

  const outletContext = useMemo(
    () => ({
      entityType,
      entityId,
      entity,
      loading,
      error,
      refresh,
      tabs,
      tabActionsSlot,
      openTab,
      openNbaPanel,
      conciergeEntity,
      nbaWorkflowsStarted,
      itemsVersion,
      // Sandbox (CT-5718): a confirmed or sent claim re-reads How to approach.
      refreshApproach,
    }),
    [
      entityType,
      entityId,
      entity,
      loading,
      error,
      refresh,
      tabs,
      tabActionsSlot,
      openTab,
      openNbaPanel,
      conciergeEntity,
      nbaWorkflowsStarted,
      itemsVersion,
      refreshApproach,
    ],
  );

  const blocked = () => {
    if (accessDenied) return <AccessDeniedState onBack={handleBackToList} />;
    if (notFound) return <NotFoundState onBack={handleBackToList} />;
    if (error) return <ErrorState message={error} onRetry={refresh} />;
    return null;
  };

  const blockedState = blocked();

  return (
    <div
      data-testid="unified-profile-shell"
      data-entity-type={entityType}
      data-entity-id={entityId}
      className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-surface-base"
    >
      <div
        ref={scrollRef}
        data-testid="unified-profile-scroll"
        className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto"
      >
        <div className="flex min-h-full flex-col">
          {/* Scrolls away with the page: the pinned bar holds identity and
              actions only, not navigation chrome. */}
          <BreadcrumbNav
            className="min-w-0 px-6 pt-6"
            items={breadcrumbItems}
            onNavigate={onBreadcrumbNavigate}
          />

          {blockedState ? null : (
            <>
              {/* Entity Header (CT-5272, Figma node 19815:101548) IS the page
                  header here — it already states the title, source, status,
                  tags and description. It has no surface of its own; per the
                  design's own rule ("it always sits in a Card Container") the
                  padding/radius/surface come from `CartContainer size="L"`.

                  Ask (CT-5721) is the primary action and is always here once
                  the record loads. `useConciergeHandoff` decides what it can
                  do: the Concierge about this record when the shell has
                  `openConcierge`, the shell chat on its Concierge until then,
                  and a toast standalone. The info button, the secondary CTA
                  and the overflow menu of the Figma spec stay unwired.

                  Vertical padding sits on the PAINTED child, never on the
                  sticky wrapper: outside it the backdrop stops at the card's
                  own box and content would scroll visibly through the gutter.
                  It is the same at rest and pinned, so compressing never moves
                  the card by itself. */}
              <div className="sticky top-0 z-20 px-6">
                <div
                  className={[
                    'relative py-4 transition-colors duration-200',
                    isScrolled ? 'bg-surface-base' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <CartContainer size="L" variant="neutral">
                    <EntityHeader
                      loading={loading}
                      compact={isScrolled}
                      avatar={{ src: entity?.visual }}
                      title={displayTitle}
                      source={entity?.source ? { label: entity.source, icon: Link2 } : undefined}
                      tags={entity?.tags}
                      status={entity?.status}
                      description={entity?.description}
                      secondaryMeta={entity?.secondaryMeta}
                      primaryAction={askAction}
                      workflow={workflowChip}
                    />
                  </CartContainer>

                  {/* Content has to leave somewhere: without this it reappears at
                      full opacity a pixel under a hard edge and the card reads as
                      clipping the tab instead of floating over it. An ABSOLUTE
                      overlay anchored just below the card (`top-full`), never an
                      in-flow sibling: compressing adds none of its 40px to the
                      pinned wrapper, so a sparse header does not shove the tab
                      strip down at the threshold. */}
                  {isScrolled ? (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-x-0 top-full h-10 bg-gradient-to-b from-surface-base to-transparent"
                    />
                  ) : null}
                </div>
              </div>

              {/* The Concierge cards (CT-5713, CT-5714): below the pinned identity, so they
                  scroll away with the page like the tab strip. */}
              <div className="px-6 pb-4">
                <ProfileSummaryCards
                  state={summary.state}
                  items={nba.items}
                  updatedAt={nba.updatedAt}
                  checkedAt={nba.checkedAt}
                  restricted={nba.restricted}
                  approach={approach.approach}
                  collapsed={summary.collapsed}
                  entity={nba.conciergeEntity}
                  onRetry={summary.retry}
                  onAction={nba.handleAction}
                  onOpenDetails={nba.openDetails}
                  onOpenSource={approach.openSource}
                />
              </div>

              {/* Built from the data. Labels come from the payload, so there is
                  no key list to keep in step with the tenants' configuration.

                  `TabNav` + `Tab`, the underline bar the rest of the platform
                  uses for page tabs — `WidgetBuilderTabs.jsx`,
                  `TruthPlaneDetailLayout.jsx`, `SandboxDetailTabs.jsx`,
                  `HtlPackDetailPage.jsx`.

                  Not `ScrollTabs`: that is the older rail `apps/agents` and
                  `apps/workspaces` still carry. It puts `role="tab"` on its
                  items but wraps them in a bare `<nav>` with no `tablist`, has
                  no arrow-key navigation, and writes its colours as raw hex
                  (`#2173FF`, `#3A3A3A`) instead of design tokens — so the
                  selected tab ignores the theme. */}
              <div className="flex items-center justify-between gap-2 px-6">
                <TabNav ariaLabel={t('profile.tabs.ariaLabel')}>
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      size="sm"
                      selected={tab.id === activeTabId}
                      // The tab id is the payload, so one handler per tab beats a
                      // shared one that has to read it back off the event target.
                      onClick={() => goToTab(tab.id)}
                    >
                      {tab.label}
                    </Tab>
                  ))}
                </TabNav>
                {/* Empty when no mounted tab has anything to portal into it
                    (a LIST tab has no toolbar of its own) — `ref` firing with
                    no children renders nothing, never an empty box with its
                    own gap. */}
                <div className="flex shrink-0 items-center gap-2">
                  {summary.canCollapse ? (
                    <SummaryCollapseToggle collapsed={summary.collapsed} onToggle={summary.toggleCollapsed} />
                  ) : null}
                  <div ref={setTabActionsSlot} className="flex shrink-0 items-center gap-2" />
                </div>
              </div>
            </>
          )}

          {/* `pt-4`: a tab's content must not start flush against the tab
              strip — 16px keeps the rhythm the header uses between its rows.

              No bottom padding here while a tab may carry a sticky footer
              (the list pager): padding below a sticky element is exactly how
              far it jumps on the last scroll step. Each tab owns its own
              `pb-6`; the blocked states, which have no footer, take it here. */}
          <div
            className={[
              'flex min-w-0 flex-1 flex-col px-6 pt-4',
              blockedState ? 'pb-6' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {blockedState ??
              (unknownTab ? (
                <Navigate to={`../${entityId}/${OVERVIEW_TAB.id}`} replace />
              ) : (
                <Outlet context={outletContext} />
              ))}
          </div>

          {/* The profile's ONE item slide-out (CT-5715). It reads `?item=` and
              portals itself, so any tab opens an item with the same link. */}
          {blockedState ? null : (
            <ProfileItemSlideOut
              entityType={entityType}
              entityId={entityId}
              entityTitle={entity?.title ?? null}
              historyTabId={historyTabId}
              openTab={openTab}
              onItemChanged={handleItemChanged}
            />
          )}
          {!blockedState && nba.panelItem ? (
            <NextBestActionSlideOut
              item={nba.panelItem}
              mode={nba.panelMode}
              items={nba.items}
              entity={nba.conciergeEntity}
              starting={nba.startingWorkflow}
              onAction={nba.handleAction}
              onStartWorkflow={nba.startWorkflow}
              onClose={nba.closePanel}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default UnifiedProfilePage;
